/* eslint-disable no-undef */
import { DrawingManager } from '@react-google-maps/api';
import { useRef, useLayoutEffect, useState, useEffect, useImperativeHandle, memo, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Stack, styled } from '@mui/material';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PolylineIcon from '@mui/icons-material/Polyline';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RectangleOutlinedIcon from '@mui/icons-material/RectangleOutlined';
import MUITooltip from 'js/components/common/MUITooltip';
import MAP, { DRAWING_MODE, DRAWING_MODE_MIXPANEL, MAP_Z_INDEX } from 'js/constants/map';
import Colors from 'js/constants/colors';
import usePropertyBoundaryLayer from '../../hooks/map/usePropertyBoundaryLayer';
import Search, { SEARCH_STATE } from 'js/components/map/tools/Search';
import { useFiltersFormContext } from 'js/components/context/FiltersFormContextProvider';
import { fetchDynamicPropertyByIds, setSelectedProperty } from 'js/actions/search';
import GeoJsonUtil from 'js/components/property/map/util/GeoJsonUtil';
import { Bbox, getSearchStringFromShapes } from 'js/helpers/MapHelper';
import CircleDeleteButton from 'js/components/map/CircleDeleteButton';
import RectangleDeleteButton from 'js/components/map/RectangleDeleteButton';
import LineDeleteButton from 'js/components/map/LineDeleteButton';
import Commons from 'js/helpers/Commons';
import { clearSearchResults } from 'js/actions/clapi';
import { ReactComponent as AddOrRemoveTool } from 'assets/svg/map/add-or-remove-tool.svg';
import { ReactComponent as CircleTool } from 'assets/svg/map/map-circle.svg';
import { ReactComponent as PanTool } from 'assets/svg/map/map-pan.svg';
import { ReactComponent as PolygonTool } from 'assets/svg/map/map-polygon.svg';
import { ReactComponent as ClearTool } from 'assets/svg/map/map-trash-bin.svg';
import { cruxLoader, cruxUnloader } from 'js/actions/crux';
import { clearDrawings, saveDrawings, setEmptyMapBubble } from 'js/actions/map';
import CustomControl from './controls/CustomControl';
import CruxDialogBox from '../common/modals/CruxDialogBox';
import { ACTIVE_COUNTRY } from 'js/helpers/Localization';
import { trackEvent } from 'js/actions/segment';
import Segment from 'js/helpers/Segment';
import MapShapeValidator from 'js/helpers/MapShapeValidator';
import segment from 'js/constants/segment';
import ListPropertyPreview from 'js/constants/listPropertyPreview';
import DynamicMapContent from '../searchResults/views/map/DynamicMapContent';
import { useMapContext } from '../context/MapContextProvider';

const TOOL_SIZE = 38;
const SEARCH = 'search';
const LEFT_TOP = 5; // window?.google?.maps?.ControlPosition.LEFT_TOP

const SHAPE_ERROR = 'The shape must have a minimum of three (3) points. Please add another to complete the boundary.';
const LINE_ERROR = 'The line must have a minimum of two (2) points. Please add another to complete the line.';
const CLASH_WITH_EXISTING_SHAPES = 'Please draw it outside the current boundaries.';
const FETCH_PROPERTY_BOUNDARY_ERROR = 'An error occurred while fetching property boundary data. Please try again.';
const NO_RESULTS = 'No property found at this location. Please review any applied filters.';
const OUT_OF_COUNTRY_ERROR = `The boundary must be within ${ACTIVE_COUNTRY.name}, please adjust any points outside of the country to perform the search`;

const StyledToolButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'specialIcon',
})((props) => ({
    fontSize: 10,
    minWidth: 0,
    width: TOOL_SIZE,
    height: TOOL_SIZE,
    '&:hover': {
        backgroundColor: props.active ? Colors.PRIMARY.MAIN : Colors.GRAY_BACKGROUND,
        ...(props.active ? {} : {
            color: props.theme.palette.primary.main,
            ...(props.specialIcon ? {
                '& svg path': {
                    fill: props.theme.palette.primary.main,
                    fillOpacity: 1,
                },
                '& svg rect': {
                    fill: props.theme.palette.primary.main,
                },
                '& svg circle': {
                    stroke: props.theme.palette.primary.main,
                    strokeOpacity: 1,
                },
            } :  {}),
        }),
    },
    color: props.active ? Colors.WHITE : Colors.SECONDARY.MAIN,
    backgroundColor: props.active ? Colors.PRIMARY.MAIN : Colors.WHITE,
    ...(props.specialIcon ? {
        '& svg path': {
            fill: props.active ? Colors.WHITE : Colors.SECONDARY.MAIN,
        },
        '& svg rect': {
            fill: props.active ? Colors.WHITE : Colors.SECONDARY.MAIN,
        },
        '& svg circle': {
            stroke: props.active ? Colors.WHITE : Colors.SECONDARY.MAIN,
        },
    } :  {}),
}));

const StyledFullPageMapToolBox = styled('div', {
    shouldForwardProp: (prop) => prop !== 'margins',
})((props) => ({
    width: TOOL_SIZE,
    marginTop: props?.margins?.top || 0,
    marginLeft: props?.margins?.left || 10,
}));

const ToolContainer = styled(({ expanded, ...props }) => (
    <div {...props}>{props.children}</div>
))(({ expanded }) => ({
    display: 'flex',
    flexWrap: 'nowrap',
    width: expanded ? 498 : TOOL_SIZE,
    transition: 'width 0.2s ease-in-out',
    '&:not(:last-child)': {
        marginBottom: 8,
    },
    '& .auto-suggest-container': {
        marginRight: 8,
        '& .search-keyword input[type="suburb-postcode-search"]': {
            height: TOOL_SIZE,
            fontSize: 13,
        },
        '& .search-keyword .search-suggestions': {
            top: TOOL_SIZE,
        }
    },
    'input[type=suburb-postcode-search]': {
        paddingRight: '12px !important',
        '&::placeholder': {
            fontSize: 11,
        },
    },
}));

const overlaysAreEqual = (prevProps, nextProps) => Commons.isEqual(prevProps.selectOptions, nextProps.selectOptions)
    && prevProps.properties === nextProps.properties
    && prevProps.drawingMode === nextProps.drawingMode
    && prevProps.overlayOptions?.showPropertyBoundariesOnLoad === nextProps.overlayOptions?.showPropertyBoundariesOnLoad;
const Overlays = memo((props) => {
    usePropertyBoundaryLayer(props);
    return <></>;
}, overlaysAreEqual);
Overlays.displayName = 'Overlays';

const MapToolbox = (props) => {
    const mapOnClickEventRef = useRef(null);
    const mounted = useRef(false);
    const isDeleteAllClicked = useRef(false);
    const shapesRef = useRef(null);
    const sphericalRef = useRef(null);
    const polyRef = useRef(null);
    const prevCoordinatesRef = useRef(null);
    const lastPropertyIdsRef = useRef(null);
    const isPropertyRemoved = useRef(false);
    const countryShape = useRef(null);
    const isRemoveBoundary = useRef(false);
    // Tracks when shapes are being cleared due to a new locality search so that
    // the shape-change useEffect can skip the redundant getProperties() call.
    const isLocalityChangeClear = useRef(false);
    // Prevents the shape-change useEffect from dispatching a second search after a handler
    // has already called getProperties() directly (immediate dispatch pattern).
    const skipShapeEffect = useRef(false);
    // Kept in sync on every render so the locality-change effect can call setMap(null)
    // on each shape without listing shape-state arrays in that effect's dep array.
    const latestAllDrawnShapesRef = useRef([]);
    // Stores the previous locality filter values to detect genuine locality changes.
    // Initialised to null so the first run always skips the clear.
    const prevLocalityFiltersRef = useRef(null);

    const [drawingMode, setDrawingMode] = useState('');
    const [searchState, setSearchState] = useState(SEARCH_STATE.hidden);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [polygon, setPolygon] = useState([]);
    const [circle, setCircle] = useState([]);
    const [polyline, setPolyline] = useState([]);
    const [rectangle, setRectangle] = useState([]);
    const [coordinates, setCoordinates] = useState([]);
    const [excludedIds, setExcludedIds] = useState([]);
    const [includedIds, setIncludedIds] = useState({});

    const {
        mapInstance,
        mapToolboxRef,
        commonMixpanel,
        searchTab,
        removeAllMapPropertyBoundaryLayers,
    } = useMapContext();

    const {
        applyFilter,
        setFilter,
        statusType,
        searchType,
        isASearchType,
        setFiltersDisabled,
        resetToDefaultMap,
        filters,
    } = useFiltersFormContext();
    const dispatch = useDispatch();
    const searchResults = useSelector(state => state.searchResults.get('searchResults'), shallowEqual);
    const activeView = useSelector(state => state.searchResults.get('activeView'), shallowEqual);
    const drawings = useSelector(state => state.map.get('drawings'), shallowEqual);
    const properties = searchResults?.data || [];
    const displayedPropertyIds = properties.map(property => property.id);

    const allDrawingsFromStore = Object.values(drawings || {}).some(
        arr => Array.isArray(arr) && arr.length > 0
    );
    const allDrawnShapes = rectangle.concat(circle).concat(polygon).concat(polyline);
    latestAllDrawnShapesRef.current = allDrawnShapes;

    const isSearchResultsAvailable = searchResults?.data && searchResults.data.length !== 0;
    const hasShapesAdded = !!allDrawnShapes.length || allDrawingsFromStore;
    const isAddressSearch = ListPropertyPreview.SEARCH_TYPE.ADDRESS === searchType;

    useEffect(() => {
        const isLastPropertyIdsInDisplayed = lastPropertyIdsRef.current
            ? Object.values(lastPropertyIdsRef.current).flat()
                .some(id => displayedPropertyIds.includes(id))
            : true;

        // prevent showing the alert when not necessary
        if (isPropertyRemoved.current || drawingMode !== DRAWING_MODE.ADD) {
            isPropertyRemoved.current = false;
            return;
        }

        if (!isLastPropertyIdsInDisplayed) {
            // prevents rejected properties from coming back on filter change
            const newIncludedIds = { ...includedIds };
            Object.keys(lastPropertyIdsRef.current).forEach((key) => {
                if (newIncludedIds[key]) {
                    delete newIncludedIds[key];
                }
            })

            setIncludedIds(newIncludedIds);
            lastPropertyIdsRef.current = null;
            props.onError('warning', NO_RESULTS);
        } else {
            // The added property was confirmed present in the latest search results.
            // Clear the pending ref so that a subsequent filter-driven searchResults change
            // (where the property may no longer appear) does not fire the NO_RESULTS toast
            // incorrectly. Without this, selecting 10,000 properties and then applying a
            // filter after an add/remove action causes a spurious "No property found" toast.
            lastPropertyIdsRef.current = null;
        }
    }, [searchResults]);

    const handleAddRemoveMixpanel = (eventName, propertyId) => {
        dispatch(trackEvent(Segment.mapAddOrRemoveProperty({
            ...commonMixpanel, eventName, propertyId,
        })));
    }

    const handleRemoveProperty = (property) => {
        const ids = property.id;
        if (drawingMode === DRAWING_MODE.ADD) {
            if (ids.some(id => Object.values(includedIds).flat().includes(id))) {
                const _includedIds = JSON.parse(JSON.stringify(includedIds));
                ids.forEach(id => {
                    Object.entries(_includedIds).forEach(([key, value]) => {
                        const idx = value.indexOf(id);
                        if (idx !== -1) {
                            value.splice(idx, 1);
                            if (value.length === 0) {
                                delete _includedIds[key];
                            }
                        }
                    });
                });
                setIncludedIds(_includedIds);
                // Also add to excludedIds so the retained snapshot strips this property on
                // the very next SET_SEARCH_FILTERS_V2 dispatch (one click = one removal).
                // Without this, the retained snapshot still carried the property because
                // mapSearch.excludedIds was empty, requiring a second click to trigger the
                // excludedIds path and actually remove the marker.
                setExcludedIds(excludedIds.concat(ids));
                // Clear lastPropertyIdsRef if it references the property being removed.
                // This prevents the "No property found" snackbar from firing after a
                // deliberate removal: the useEffect([searchResults]) guard
                // (isPropertyRemoved.current) can be consumed by an intermediate render,
                // so clearing the ref here ensures isLastPropertyIdsInDisplayed is treated
                // as true (null → true) and the snackbar check is skipped entirely.
                if (lastPropertyIdsRef.current) {
                    const removedSet = new Set(ids);
                    const lastIds = Object.values(lastPropertyIdsRef.current).flat();
                    if (lastIds.some(id => removedSet.has(id))) {
                        lastPropertyIdsRef.current = null;
                    }
                }
            } else {
                setExcludedIds(excludedIds.concat(ids));
            }
            handleAddRemoveMixpanel('Remove Property', ids);
            isPropertyRemoved.current = true;
        } else {
            dispatch(setSelectedProperty([property]));
        }
    };

    // if needed we can implement a context for this method.
    useImperativeHandle(mapToolboxRef, () => ({
        excludeFromSearch: handleRemoveProperty,
        setDrawingMode,
        getDrawingMode: () => drawingMode,
        getIncludedIds: () => includedIds,
    }));

    useLayoutEffect(() => {
        if (drawingMode === SEARCH) {
            setTimeout(() => {
                setSearchState(SEARCH_STATE.visible);
            }, 200);
        } else if (searchState !== SEARCH_STATE.hidden) {
            setSearchState(SEARCH_STATE.hidden);
        }
    }, [drawingMode]);

    const toCruxCircle = _circle => _circle && ({
        radius: _circle.getRadius(),
        center: {
            lat: _circle.getCenter().lat(),
            lon: _circle.getCenter().lng(),
        },
    });

    const toCruxLatLon = _poly => _poly?.getPath().getArray().map(latLng => ({
        lat: latLng.lat(),
        lon: latLng.lng(),
    }));

    const handleOnToolSelect = (tool, callback) => () => {
        const isSearchTool = tool === SEARCH;
        const isSearchOpen = searchState === SEARCH_STATE.visible;

        if (isSearchTool) {
            // toggle search open/close
            setDrawingMode(isSearchOpen ? '' : SEARCH);
            setSearchState(isSearchOpen ? SEARCH_STATE.hidden : SEARCH_STATE.visible);
        } else {
            // close search if open, then set the chosen tool
            if (isSearchOpen) setSearchState(SEARCH_STATE.hidden);
            setDrawingMode(tool);
        }

        callback?.();
    };

    const deleteAllAction = () => {
        allDrawnShapes.forEach((shape) => {
            shape.setMap(null);
        });

        setCircle([]);
        setPolygon([]);
        setPolyline([]);
        setRectangle([]);
        setCoordinates([]);
        setExcludedIds([]);
        setIncludedIds([]);
        setDrawingMode('');
        props.events?.delete?.onSelect?.();

        isDeleteAllClicked.current = true;
    }

    const buildTools = useCallback(() => {
        const _tools = [
            {
                icon: PanTool,
                key: '',
                name: 'pan',
                specialIcon: true,
                onClick: () => {
                    setDrawingMode('');
                    setSearchState(SEARCH_STATE.hidden);
                },
                tip: 'Pan',
            },
        ];

        if (props.options?.tools?.search?.enabled === undefined
            || props.options?.tools?.search?.enabled) {
            _tools.push({
                icon: searchState === SEARCH_STATE.visible ? CloseIcon : SearchOutlinedIcon,
                key: SEARCH,
                name: SEARCH,
                onClick: handleOnToolSelect(SEARCH, props.events?.search?.onSelect),
                tip: searchState === SEARCH_STATE.visible ? 'Close' : 'Search',
            });
        }

        if (props.options?.tools?.polyline?.enabled === undefined
            || props.options?.tools?.polyline?.enabled) {
            _tools.push({
                icon: PolylineIcon,
                key: DRAWING_MODE.POLYLINE,
                name: DRAWING_MODE.POLYLINE,
                onClick: handleOnToolSelect(DRAWING_MODE.POLYLINE, props.events?.polyline?.onSelect),
                specialIcon: true,
                tip: 'Draw Line',
            });
        }

        if (props.options?.tools?.polygon?.enabled === undefined
            || props.options?.tools?.polygon?.enabled) {
            _tools.push({
                icon: PolygonTool,
                key: DRAWING_MODE.POLYGON,
                name: DRAWING_MODE.POLYGON,
                onClick: handleOnToolSelect(DRAWING_MODE.POLYGON, props.events?.polygon?.onSelect),
                disabled: props.options?.tools?.polygon?.disabled,
                specialIcon: true,
                tip: 'Draw Shape',
            });
        }

        if (props.options?.tools?.circle?.enabled === undefined
            || props.options?.tools?.circle?.enabled) {
            _tools.push({
                icon: CircleTool,
                key: DRAWING_MODE.CIRCLE,
                name: DRAWING_MODE.CIRCLE,
                onClick: handleOnToolSelect(DRAWING_MODE.CIRCLE, props.events?.circle?.onSelect),
                disabled: props.options?.tools?.circle?.disabled,
                specialIcon: true,
                tip: 'Draw Circle',
            });
        }

        if (props.options?.tools?.rectangle?.enabled === undefined
            || props.options?.tools?.rectangle?.enabled) {
            _tools.push({
                icon: RectangleOutlinedIcon,
                key: DRAWING_MODE.RECTANGLE,
                name: DRAWING_MODE.RECTANGLE,
                onClick: handleOnToolSelect(DRAWING_MODE.RECTANGLE, props.events?.rectangle?.onSelect),
                disabled: props.options?.tools?.rectangle?.disabled,
                specialIcon: true,
                tip: 'Draw Rectangle',
            })
        }

        if (props.options?.tools?.addOrRemove?.enabled === undefined
            || props.options?.tools?.addOrRemove?.enabled) {
            _tools.push({
                icon: AddOrRemoveTool,
                key: DRAWING_MODE.ADD,
                name: DRAWING_MODE.ADD,
                onClick: handleOnToolSelect(DRAWING_MODE.ADD, props.events?.addOrRemove?.onSelect),
                specialIcon: true,
                tip: 'Add/Remove Property',
            });
        }

        if (props.options?.tools?.undo?.enabled === undefined
            || props.options?.tools?.undo?.enabled) {
            _tools.push({
                icon: UndoIcon,
                key: 'undo',
                name: 'undo',
                disabled: props.options?.tools?.undo?.disabled,
                onClick: () => {
                    props.events?.undo?.onSelect?.();
                },
                tip: 'Undo',
            });
        }

        if (props.options?.tools?.redo?.enabled === undefined
            || props.options?.tools?.redo?.enabled) {
            _tools.push({
                icon: RedoIcon,
                key: 'redo',
                name: 'redo',
                disabled: props.options?.tools?.redo?.disabled,
                onClick: () => {
                    props.events?.redo?.onSelect?.();
                },
                tip: 'Redo',
            });
        }

        if (props.options?.tools?.delete?.enabled === undefined
            || props.options?.tools?.delete?.enabled) {
            _tools.push({
                icon: ClearTool,
                key: 'delete',
                name: 'delete',
                disabled: props.options?.tools?.delete?.disabled || (props.isMapSearch && !allDrawnShapes?.concat(Object.values(includedIds))?.length),
                specialIcon: true,
                onClick: () => {
                    if (props.isMapSearch) {
                        setIsModalOpen(true);
                    } else {
                        deleteAllAction();
                    }
                },
                tip: props.isMapSearch ? 'Delete All' : 'Delete Shape',
            });
        }

        return _tools
    }, [props.options?.tools, props.events, searchState, drawingMode]);

    const tools = buildTools();

    const trackMapEvents = ({ circleOverride, polygonOverride, rectangleOverride } = {}) => {
        // not available with delete boundary, add & remove property, and delete all
        if (!isRemoveBoundary.current && drawingMode !== DRAWING_MODE.ADD && !isDeleteAllClicked.current) {
            const shapeForSearchString = {
                rectangle: rectangleOverride ?? rectangle,
                circle: circleOverride ?? circle,
                polygon: polygonOverride ?? polygon,
                polyline,
            };
            const searchString = getSearchStringFromShapes(shapeForSearchString);
            dispatch(trackEvent(Segment.mapPropertySearch({
                ...commonMixpanel,
                drawingMode: searchString?.join(', '),
            })));
        }

        if (isDeleteAllClicked.current) {
            dispatch(trackEvent(Segment.mapCommonEvent({
                ...commonMixpanel,
                eventName: segment.EVENT_NAMES.DELETE_ALL,
            })));
        }
    }

    const getProperties = ({ circleOverride, polygonOverride, rectangleOverride } = {}) => {
        const _circle = circleOverride ?? circle;
        const _polygon = (polygonOverride ?? polygon).map(toCruxLatLon);
        const _rectangle = (rectangleOverride ?? rectangle).map(_rect => Bbox.toArray(_rect.getBounds()));
        dispatch(setEmptyMapBubble());
        applyFilter?.({
            isNonAddressSearch: true,
            mapSearch: {
                polygon: _polygon,
                rectangle: _rectangle,
                circle: _circle.map(toCruxCircle),
                propertyIds: includedIds, // ids to be included in the search
                excludedIds,
                skipLogFilters: true,
                searchType,
                // Signal to the reducer that a shape was explicitly deleted so it clears
                // retainedSearchResults instead of snapshotting. Without this flag the
                // reducer would snapshot the current combined data (which includes the
                // deleted shape's properties), causing those markers to persist on the map
                // even after the shape is gone.
                isRemoveBoundary: isRemoveBoundary.current,
            },
        });

        trackMapEvents({ circleOverride, polygonOverride, rectangleOverride });

        isRemoveBoundary.current = false;
        isDeleteAllClicked.current = false;
    }

    const removeDuplicatePropertyIds = (_ids = []) => {
        const flattenedIncludedIds = Object.values(includedIds).flat();
        const includedIdsSet = new Set(flattenedIncludedIds);
        return _ids.filter(id => !includedIdsSet.has(id));
    }

    const handleOverlayOnFetchError = () => {
        dispatch(cruxUnloader());
        setCoordinates([]);
        props.onError('error', FETCH_PROPERTY_BOUNDARY_ERROR);
    }

    const completeOverlayFetch = () => {
        // Only update state when there is something to clear.
        // Calling setCoordinates([]) with a new [] reference when coordinates is
        // already empty causes an unnecessary re-render of MapToolbox, which
        // recreates the inline selectOptions.coordinates object passed to Overlays
        // and triggers a duplicate ESRI API call via usePropertyBoundaryLayer.
        if (coordinates.length) setCoordinates([]);
        dispatch(cruxUnloader());
    };

    const handlePolylineFetchSuccess = (response) => {
        const originalIds = response.features.map(feature => feature.properties.property_id);
        const uniqueIds = removeDuplicatePropertyIds(originalIds);
        const toBeDisplayedIds = uniqueIds.filter(id => !displayedPropertyIds.includes(id));

        setIncludedIds({
            ...includedIds,
            [polyline.at(-1)?.id]: toBeDisplayedIds,
        });
    };

    const handleAddPropertyFetchSuccess = (response) => {
        const originalIds = response.features.map(feature => feature.properties.property_id);
        const uniqueIds = removeDuplicatePropertyIds(originalIds);
        const toBeDisplayedIds = uniqueIds.filter(id => !displayedPropertyIds.includes(id));

        if (toBeDisplayedIds.length) {
            const newIncludedIds = {
                [JSON.stringify(coordinates.at(-1))]: toBeDisplayedIds,
            };
            setIncludedIds({
                ...includedIds,
                ...newIncludedIds,
            });
            if (excludedIds.length) {
                const reAddedSet = new Set(toBeDisplayedIds);
                setExcludedIds(excludedIds.filter(id => !reAddedSet.has(id)));
            }
            lastPropertyIdsRef.current = newIncludedIds;
            handleAddRemoveMixpanel('Add Property', toBeDisplayedIds);
            return;
        }

        if (!originalIds.length) {
            dispatch(setEmptyMapBubble({ coordinates: coordinates.at(-1) }));
        }
    };

    const handlePanModeFetchSuccess = (response) => {
        const propertyIds = GeoJsonUtil.extractPropertyIds(response);
        dispatch(fetchDynamicPropertyByIds({
            propertyIds: propertyIds ?? [],
            mixpanel: commonMixpanel,
            isRadiusSearch: props.dynamicContent?.isRadiusSearch,
        }));
    };

    const handleOverlayOnFetchSuccess = (response) => {
        if (drawingMode === DRAWING_MODE.POLYLINE && response) {
            handlePolylineFetchSuccess(response);
        } else if (drawingMode === DRAWING_MODE.ADD && response) {
            handleAddPropertyFetchSuccess(response);
        } else if (drawingMode === '' && !props.dynamicContent?.isFromCardSelection) {
            // Only trigger pan-mode fetch when the overlay was triggered by a direct map click,
            // not by a card selection (which already has all needed data in selectedProperty).
            handlePanModeFetchSuccess(response);
        }
        completeOverlayFetch();
    };

    const handleOnOverlaySelect = (e) => {
        if (drawingMode === DRAWING_MODE.ADD) {
            removeAllMapPropertyBoundaryLayers();
            handleRemoveProperty({
                id: [e.feature.getProperty('property_id')],
            });
        } else {
            props.onPropertyBoundaryClick?.();
        }
    }

    const handleOnAddProperty = (event) => {
        const newCoordinates = {
            lat: event.latLng?.lat(),
            lon: event.latLng?.lng(),
        }

        // prevents infinite loading when same point is clicked multiple times
        if (!Commons.isEqual(prevCoordinatesRef.current, newCoordinates)) {
            prevCoordinatesRef.current = newCoordinates;
            setCoordinates(coordinates.concat([newCoordinates]));
            dispatch(cruxLoader());
        }
    };

    const handleOnRemoveDrawing = (shape, setShapes, eventType) => () => {
        shape.setMap(null);
        setDrawingMode('');
        setShapes(current => current.filter(s => s !== shape));
        isRemoveBoundary.current = true;
        dispatch(trackEvent(Segment.mapCommonEvent({
            ...commonMixpanel,
            eventName: segment.EVENT_NAMES.DELETE_BOUNDARY,
            objectContext: DRAWING_MODE_MIXPANEL[eventType],
        })));
    }

    const handleShapeError = (_shape, error = CLASH_WITH_EXISTING_SHAPES) => {
        _shape.setMap(null);
        props.onError('error', error);
        setDrawingMode('');
    }

    const handleCreateBoundaryMixpanel = (eventType) => {
        if (props.isMapSearch) {
            dispatch(trackEvent(Segment.mapCommonEvent({
                ...commonMixpanel,
                eventName: segment.EVENT_NAMES.CREATE_BOUNDARY,
                objectContext: DRAWING_MODE_MIXPANEL[eventType],
            })));
        }
    }

    const handlePolygonComplete = useCallback((_polygon) => {
        if (_polygon.getPath().length < 3) {
            handleShapeError(_polygon, SHAPE_ERROR);
            return;
        }

        if (!MapShapeValidator.isInsideTheCountry(
            _polygon,
            DRAWING_MODE.POLYGON,
            countryShape.current,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_polygon, OUT_OF_COUNTRY_ERROR);
            return;
        }

        const localityBoundary = props.events?.locality?.getBoundary?.();
        if (MapShapeValidator.isShapeInsideExistingShapes(
            _polygon,
            DRAWING_MODE.POLYGON,
            {
                circles: circle,
                polygons: polygon,
                rectangles: rectangle,
                polylines: polyline
            },
            localityBoundary,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_polygon);
            return;
        }

        handleCreateBoundaryMixpanel(DRAWING_MODE.POLYGON);
        const newPolygons = polygon.concat([_polygon]);
        skipShapeEffect.current = true;
        setPolygon(newPolygons);
        setDrawingMode('');
        if (props.isMapSearch) getProperties({ polygonOverride: newPolygons });
    }, [circle, polygon, rectangle, polyline]);

    const handleCircleComplete = useCallback((_circle) => {
        if (!MapShapeValidator.isInsideTheCountry(
            _circle,
            DRAWING_MODE.CIRCLE,
            countryShape.current,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_circle, OUT_OF_COUNTRY_ERROR);
            return;
        }

        const localityBoundary = props.events?.locality?.getBoundary?.();
        if (MapShapeValidator.isShapeInsideExistingShapes(
            _circle,
            DRAWING_MODE.CIRCLE,
            {
                circles: circle,
                polygons: polygon,
                rectangles: rectangle,
                polylines: polyline
            },
            localityBoundary,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_circle);
            return;
        }

        if (!props.options?.tools.circle?.multiple) {
            circle.at(-1)?.setMap(null);
            skipShapeEffect.current = true;
            setCircle([_circle]);
            if (props.isMapSearch) getProperties({ circleOverride: [_circle] });
        } else {
            const newCircles = circle.concat([_circle]);
            skipShapeEffect.current = true;
            setCircle(newCircles);
            if (props.isMapSearch) getProperties({ circleOverride: newCircles });
        }

        handleCreateBoundaryMixpanel(DRAWING_MODE.CIRCLE);
        setDrawingMode('');
    }, [circle, polygon, rectangle, polyline, props.options?.tools?.circle?.multiple]);

    const handlePolylineComplete = useCallback((_polyline) => {
        if (!MapShapeValidator.isInsideTheCountry(
            _polyline,
            DRAWING_MODE.POLYLINE,
            countryShape.current,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_polyline, OUT_OF_COUNTRY_ERROR);
            return;
        }

        const localityBoundary = props.events?.locality?.getBoundary?.();
        const lineIsInsideShape = MapShapeValidator.isShapeInsideExistingShapes(
            _polyline,
            DRAWING_MODE.POLYLINE,
            {
                circles: circle,
                polygons: polygon,
                rectangles: rectangle,
                polylines: polyline
            },
            localityBoundary,
            polyRef.current,
            sphericalRef.current
        );
        const invalidLine = _polyline.getPath().getLength() <= 1;
        if (lineIsInsideShape || invalidLine) {
            const errorMessage = lineIsInsideShape ? CLASH_WITH_EXISTING_SHAPES : LINE_ERROR;
            handleShapeError(_polyline, errorMessage);
            return;
        }

        _polyline.id = `polyline-${polyline.length}`;
        setPolyline(polyline.concat([_polyline]));
        dispatch(cruxLoader());
        handleCreateBoundaryMixpanel(DRAWING_MODE.POLYLINE);
        setDrawingMode('');
    }, [circle, polygon, rectangle, polyline, dispatch]);

    const handleRectangleComplete = useCallback((_rectangle) => {
        if (!MapShapeValidator.isInsideTheCountry(
            _rectangle,
            DRAWING_MODE.RECTANGLE,
            countryShape.current,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_rectangle, OUT_OF_COUNTRY_ERROR);
            return;
        }

        const localityBoundary = props.events?.locality?.getBoundary?.();
        if (MapShapeValidator.isShapeInsideExistingShapes(
            _rectangle,
            DRAWING_MODE.RECTANGLE,
            {
                circles: circle,
                polygons: polygon,
                rectangles: rectangle,
                polylines: polyline
            },
            localityBoundary,
            polyRef.current,
            sphericalRef.current
        )) {
            handleShapeError(_rectangle);
            return;
        }

        handleCreateBoundaryMixpanel(DRAWING_MODE.RECTANGLE);
        const newRectangles = rectangle.concat(_rectangle);
        skipShapeEffect.current = true;
        setRectangle(newRectangles);
        setDrawingMode('');
        if (props.isMapSearch) getProperties({ rectangleOverride: newRectangles });
    }, [circle, polygon, rectangle, polyline]);

    const handleOverlayComplete = useCallback((event) => {
        props.events?.onOverlayComplete?.(event);
    }, [props.events]);

    useEffect(() => {
        if (shapesRef.current) {
            dispatch(saveDrawings(shapesRef.current));
        }
    }, [activeView, statusType]);

    // Locality-filter primitives used as deps so ESLint exhaustive-deps is satisfied
    // without adding the entire filters object (which changes reference on every render).
    const localityAddressSuburb = filters?.addressSuburb;
    const localityAddressStreet = filters?.addressStreet;
    const localityAddressTown = filters?.addressTown;
    const localityCouncilArea = filters?.councilArea;
    const localityAddressState = filters?.addressState;
    const localityPostcode = filters?.postcode;

    // When the locality (suburb, street, council area, etc.) changes while shapes are
    // drawn, clear those shapes so they are not carried over to the new search area.
    // The flag isLocalityChangeClear prevents getProperties() from firing a redundant
    // search in the shape-change effect below — the locality search is already dispatched
    // by FiltersFormContextProvider.resetToDefault.
    useEffect(() => {
        const currLocality = {
            addressSuburb: localityAddressSuburb,
            addressStreet: localityAddressStreet,
            addressTown: localityAddressTown,
            councilArea: localityCouncilArea,
            addressState: localityAddressState,
            postcode: localityPostcode,
        };

        const prevLocality = prevLocalityFiltersRef.current;
        // Always update the ref before returning so the next run has a valid baseline.
        prevLocalityFiltersRef.current = currLocality;

        // Skip on the first run (no previous locality to compare against).
        if (prevLocality === null) return;

        if (!Commons.isEqual(prevLocality, currLocality) && latestAllDrawnShapesRef.current.length) {
            isLocalityChangeClear.current = true;
            latestAllDrawnShapesRef.current.forEach(shape => shape.setMap(null));
            setCircle([]);
            setPolygon([]);
            setPolyline([]);
            setRectangle([]);
            setCoordinates([]);
            setExcludedIds([]);
            setIncludedIds({});
            setDrawingMode('');
        }
    }, [
        localityAddressSuburb,
        localityAddressStreet,
        localityAddressTown,
        localityCouncilArea,
        localityAddressState,
        localityPostcode,
    ]);

    useEffect(() => {
        // A shape handler (circle/polygon/rectangle) already called getProperties() directly
        // to dispatch the search immediately. Skip here to prevent a double-dispatch.
        if (skipShapeEffect.current) {
            skipShapeEffect.current = false;
            return;
        }

        if (mounted.current && props.isMapSearch) {
            // Shapes were cleared because the locality changed (new search area). The new
            // search has already been dispatched by FiltersFormContextProvider, so skip
            // getProperties() here to avoid a redundant / incorrect second search.
            if (isLocalityChangeClear.current) {
                isLocalityChangeClear.current = false;
                return;
            }

            if (setFilter && isDeleteAllClicked.current) {
                setFilter({
                    mapSearch: null,
                    propertyId: null,
                });
                dispatch(clearSearchResults());
            }

            getProperties();
        }
    }, [circle, polygon, includedIds, rectangle, excludedIds]);

    useEffect(() => {
        // shapesRef.current should only have value when it's a map search
        if (props.isMapSearch) {
            shapesRef.current = {
                polygon: polygon?.map(_polygon => _polygon?.getPath().getArray()),
                polyline: polyline?.map(_polyline => _polyline?.getPath().getArray()),
                rectangle: rectangle?.map(_rectangle => _rectangle.getBounds()),
                circle: circle?.map(_circle => ({
                    radius: _circle.getRadius(),
                    center: _circle.getCenter(),
                })),
                coordinates,
                includedIds,
                excludedIds,
            };
        }
    }, [polygon, polyline, rectangle, circle, coordinates, includedIds, excludedIds]);

    useEffect(() => {
        if (drawingMode === DRAWING_MODE.ADD) {
            mapOnClickEventRef.current?.remove?.();
            mapOnClickEventRef.current = mapInstance?.addListener('click', handleOnAddProperty);
        } else if (drawingMode === '' && props.events?.map?.onClick) {
            mapOnClickEventRef.current?.remove?.();
            mapOnClickEventRef.current = mapInstance?.addListener('click', props.events.map.onClick);
        }

        return () => {
            mapOnClickEventRef.current?.remove?.();
        };
    }, [drawingMode, mapInstance, props.events?.map]);

    useEffect(() => {
        if (mounted.current) {
            props.events?.drawingTool?.onChange?.();
        }
    }, [drawingMode]);

    useEffect(() => {
        if (!!drawings && props.isMapSearch) {
            google?.maps?.importLibrary('maps')
                .then(({ Polyline, Polygon, Circle, Rectangle }) => {
                    setCircle(drawings.circle?.map((_circle) => {
                        return new Circle({
                            ...MAP.POLYGON.OPTIONS.OUTLINE,
                            ..._circle,
                            map: mapInstance,
                        });
                    }));
                    setRectangle(drawings.rectangle?.map((_rectangle) => {
                        return new Rectangle({
                            ...MAP.POLYGON.OPTIONS.OUTLINE,
                            bounds: _rectangle,
                            map: mapInstance,
                        });
                    }));
                    setPolygon(drawings.polygon?.map((_polygon) => {
                        return new Polygon({
                            ...MAP.POLYGON.OPTIONS.OUTLINE,
                            paths: _polygon,
                            map: mapInstance,
                        });
                    }));
                    setPolyline(drawings.polyline?.map((_polyline, index) => {
                        const gMapPolyline = new Polyline({
                            ...MAP.POLYGON.OPTIONS.OUTLINE,
                            path: _polyline,
                            map: mapInstance,
                        });
                        gMapPolyline.id = `polyline-${index}`;
                        return gMapPolyline;
                    }));
                    setIncludedIds(drawings.includedIds);
                    setExcludedIds(drawings.excludedIds);
                })
                .catch(error => console.debug('Error in map toolbox', error))
                .finally(() => {
                    dispatch(clearDrawings());
                    mounted.current = true;
                });
        } else {
            mounted.current = true;
        }
        google?.maps?.importLibrary('geometry')
            .then(({ spherical, poly }) => {
                sphericalRef.current = spherical;
                polyRef.current = poly;
            });
        return () => {
            dispatch(saveDrawings(shapesRef.current))
        }
    }, []);

    useEffect(() => {
        if (!countryShape.current) {
            countryShape.current = new google.maps.Polygon({
                ...MAP.POLYGON.OPTIONS.OUTLINE,
                // comment out the stroke styling for debugging purposes
                strokeColor: 'transparent',
                strokeOpacity: 0,
                strokeWeight: 0,
                map: mapInstance,
                paths: ACTIVE_COUNTRY.MAP_SEARCH.COUNTRY_POLYGON_PATHS,
            });
        }
    }, []);

    useEffect(() => {
        if (isAddressSearch && !isSearchResultsAvailable && !isASearchType() && !hasShapesAdded) {
            setFiltersDisabled?.(true);
            resetToDefaultMap();
        } else {
            setFiltersDisabled?.(false);
        }
    }, [searchResults, hasShapesAdded]);

    return (<>
        <DrawingManager
            drawingMode={[DRAWING_MODE.ADD, 'search'].includes(drawingMode) ? null : drawingMode}
            options={{
                drawingControl: false,
                polygonOptions: MAP.POLYGON.OPTIONS.OUTLINE,
                polylineOptions: MAP.POLYGON.OPTIONS.OUTLINE,
                circleOptions: MAP.POLYGON.OPTIONS.OUTLINE,
                rectangleOptions: MAP.POLYGON.OPTIONS.OUTLINE,
            }}
            onOverlayComplete={handleOverlayComplete}
            onPolygonComplete={handlePolygonComplete}
            onCircleComplete={handleCircleComplete}
            onPolylineComplete={handlePolylineComplete}
            onRectangleComplete={handleRectangleComplete}
        />
        <CustomControl
            position={LEFT_TOP}
        >
            <StyledFullPageMapToolBox margins={props.margins}>
                <Stack justifyContent="space-between" direction="column">
                    {
                        tools.map(({ icon: Icon, key, onClick, disabled, name, specialIcon, tip }) => (
                            <ToolContainer
                                key={name}
                                expanded={key === SEARCH && searchState === SEARCH_STATE.visible}
                            >
                                {
                                    key === SEARCH &&
                                    <Search
                                        map={mapInstance}
                                        searchState={searchState}
                                    />
                                }
                                <MUITooltip
                                    title={tip}
                                    arrow
                                    placement="right"
                                    key={`mui-tooltip-${name}-${drawingMode}`}
                                    sx={{ zIndex: MAP_Z_INDEX }}
                                >
                                    <span data-testid={`tooltip-trigger-${name}`}>
                                        <StyledToolButton
                                            color="secondary"
                                            variant="contained"
                                            aria-label={name}
                                            active={(key === SEARCH && searchState === SEARCH_STATE.visible)
                                                || key === drawingMode}
                                            onClick={onClick}
                                            disabled={disabled}
                                            sx={key === DRAWING_MODE.POLYGON ? { padding: 0 } : {}}
                                            specialIcon={specialIcon}
                                        >
                                            <Icon sx={{ color: Colors.BLACK_80_OPAQUE }}/>
                                        </StyledToolButton>
                                    </span>
                                </MUITooltip>
                            </ToolContainer>
                        ))
                    }
                </Stack>
            </StyledFullPageMapToolBox>
        </CustomControl>
        {
            props.options?.tools?.circle?.enabled
            && props.options?.tools?.circle?.allowDelete
            && circle.map((shape, index) => {
                return (<CircleDeleteButton
                    key={`circle-${JSON.stringify({
                        lat: shape.getCenter().lat(),
                        lng: shape.getCenter().lng(),
                    })}-${index}`}
                    shape={shape}
                    map={mapInstance}
                    onClick={handleOnRemoveDrawing(shape, setCircle, DRAWING_MODE.CIRCLE)}
                />)
            })
        }
        {
            props.options?.tools?.rectangle?.enabled
            && props.options?.tools?.rectangle?.allowDelete
            && rectangle.map((shape, index) => {
                return (<RectangleDeleteButton
                    key={`rectangle-${JSON.stringify({
                        lat: shape.getBounds().getNorthEast().lat(),
                        lng: shape.getBounds().getSouthWest().lng(),
                    })}-${index}`}
                    shape={shape}
                    map={mapInstance}
                    onClick={handleOnRemoveDrawing(shape, setRectangle, DRAWING_MODE.RECTANGLE)}
                />)
            })
        }
        {
            props.options?.tools?.polyline?.enabled
            && props.options?.tools?.polyline?.allowDelete
            && polyline.map((shape, index) => {
                return (<LineDeleteButton
                    key={`line-${JSON.stringify({
                        lat: shape?.getPath()?.getArray()[0].lat(),
                        lng: shape?.getPath()?.getArray()[0].lng(),
                    })}-${index}`}
                    position={shape?.getPath()?.getArray()[0]}
                    map={mapInstance}
                    onClick={() => {
                        handleOnRemoveDrawing(shape, setPolyline, DRAWING_MODE.POLYLINE)();
                        setIncludedIds(current => {
                            const _current = JSON.parse(JSON.stringify(current));
                            delete _current[shape.id];
                            return _current;
                        });
                    }}
                />)
            })
        }
        {
            props.options?.tools?.polygon?.enabled
            && props.options?.tools?.polygon?.allowDelete
            && polygon.map((shape, index) => {
                return (<LineDeleteButton
                    key={`polygon-${JSON.stringify({
                        lat: shape?.getPath()?.getArray()[0].lat(),
                        lng: shape?.getPath()?.getArray()[0].lng(),
                    })}-${index}`}
                    map={mapInstance}
                    position={shape?.getPath()?.getArray()[0]}
                    onClick={handleOnRemoveDrawing(shape, setPolygon, DRAWING_MODE.POLYGON)}
                />)
            })
        }
        {
            props.dynamicContent &&
            <DynamicMapContent
                getLocalityBoundary={props.dynamicContent.getLocalityBoundary}
                getIncludedIds={() => includedIds}
                searchType={searchType}
                searchTab={searchTab}
                onBubbleClose={props.dynamicContent.onBubbleClose}
            />
        }
        {
            // need result from esri to get property id
            !!props.options?.handleLayers &&
            <Overlays
                map={mapInstance}
                properties={properties}
                drawingMode={drawingMode}
                selectOptions={{
                    polyline: polyline.length ? toCruxLatLon(polyline?.at(-1)) : null,
                    coordinates: (drawingMode === '' && props.dynamicContent?.targetCoordinate)
                        ? [{
                            lat: props.dynamicContent?.targetCoordinate?.lat,
                            lon: props.dynamicContent?.targetCoordinate?.lon ?? props.dynamicContent?.targetCoordinate?.lng
                        }] : (coordinates.length ? [coordinates?.at(-1)] : null),
                }}
                overlayOptions={{
                    latestOnly: false,
                    showPropertyBoundariesOnLoad: !!props.dynamicContent?.targetCoordinate,
                    onFetchSuccess: handleOverlayOnFetchSuccess,
                    onFetchError: handleOverlayOnFetchError,
                    events: {
                        click: drawingMode === DRAWING_MODE.ADD ? handleOnOverlaySelect : null,
                    }
                }}
            />
        }
        <CruxDialogBox
            id="delete-shape-dialog"
            type="Delete"
            title="Are you sure you want to delete all boundaries?"
            open={isModalOpen}
            onConfirm={deleteAllAction}
            onClose={() => setIsModalOpen(false)}
            subTitle="This will remove all drawn boundaries and manually selected properties and cannot be undone."
        />
    </>);
};

// Reusable PropType shapes
const ToolConfigShape = PropTypes.shape({
    enabled: PropTypes.bool,
});

const DrawingToolConfigShape = PropTypes.shape({
    enabled: PropTypes.bool,
    multiple: PropTypes.bool,
    allowDelete: PropTypes.bool,
});

const ToolEventShape = PropTypes.shape({
    onSelect: PropTypes.func,
});

const CoordinateShape = PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
    lng: PropTypes.number, // Alternative to lon
});

MapToolbox.propTypes = {
    // Core functionality flags
    isMapSearch: PropTypes.bool,

    // Configuration options
    options: PropTypes.shape({
        handleLayers: PropTypes.bool,
        tools: PropTypes.shape({
            // Search tool
            search: ToolConfigShape,

            // Drawing tools with full configuration
            polyline: DrawingToolConfigShape,
            polygon: DrawingToolConfigShape,
            circle: DrawingToolConfigShape,
            rectangle: DrawingToolConfigShape,

            // Action tools
            addOrRemove: ToolConfigShape,
            undo: ToolConfigShape,
            redo: ToolConfigShape,
            delete: ToolConfigShape,
        }),
    }),

    // Event handlers
    events: PropTypes.shape({
        onOverlayComplete: PropTypes.func,
        map: PropTypes.shape({
            onClick: PropTypes.func,
        }),
        search: ToolEventShape,
        polyline: ToolEventShape,
        polygon: ToolEventShape,
        circle: ToolEventShape,
        rectangle: ToolEventShape,
        addOrRemove: ToolEventShape,
        undo: ToolEventShape,
        redo: ToolEventShape,
        delete: ToolEventShape,
        drawingTool: PropTypes.shape({
            onChange: PropTypes.func,
        }),
        locality: PropTypes.shape({
            getBoundary: PropTypes.func,
        }),
    }),

    // Layout configuration
    margins: PropTypes.shape({
        top: PropTypes.number,
        left: PropTypes.number,
    }),

    // Callback functions
    onError: PropTypes.func,
    onPropertyBoundaryClick: PropTypes.func,

    // Dynamic content configuration
    dynamicContent: PropTypes.shape({
        onBubbleClose: PropTypes.func,
        targetCoordinate: CoordinateShape,
        isFromCardSelection: PropTypes.bool,
    }),
}

export default MapToolbox;